import * as React from 'react'
import { formatDriverName } from '../../utils/driverName'
import { useDriverForm } from '../../context/DriverFormContext'
import BasicForm from '../../forms/driver/basic/BasicForm'
import AddDriverFooter from './AddDriverFooter'
import useDriverService from '../../services/DriverService'
import GroupsModal from '../groups/GroupsModal'
import { GroupsFormProvider } from '../../context/GroupsFormContext'
import { toast } from '@/lib/toasts/api'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useLoadingStore, selectLoading } from '@/modules/shared/stores/loadingStore'
import { LoaderIcon } from '@/app/features/icons/Loader'
import AddDriverHeader from './AddDriverHeader'
import FullFormAdditional from '../../forms/driver/additional/FullFormAdditional'
import FullFormBasic from '../../forms/driver/full/FullFormBasic'
import ModalLayout from '../shared/ModalLayout'
import { useDrivers } from '@/modules/shared/context/drivers/DriversContext'
import { DriverAddForm } from '../../types/driver'

const extractCreatedDriverId = (res: any): number | null => {
  const raw =
    res?.data?.driverId?.value ?? res?.data?.driverId ?? res?.data?.id
  const n = Number(raw)
  return Number.isFinite(n) && n > 0 ? n : null
}

const nonEmpty = (v?: string | null) => !!(v && v.trim().length > 0)

const hasPesel = (data: DriverAddForm | null) =>
  nonEmpty(data?.pesel) || nonEmpty(data?.driverId?.value)

const hasCardNumber = (data: DriverAddForm | null) =>
  nonEmpty(data?.driverCard?.cardNumber?.value)

const isDriverFormValid = (
  data: DriverAddForm | null,
  formType: string,
): boolean => {
  switch (formType) {
    case 'simple':
      return (
        nonEmpty(data?.name) &&
        nonEmpty(data?.surname) &&
        hasPesel(data) &&
        nonEmpty(data?.drivingLicenceNumber) &&
        hasCardNumber(data)
      )
    case 'full-basic':
      return hasPesel(data) && hasCardNumber(data)
    default:
      return true
  }
}

const AddDriverModal = ({
  isOpen,
  close,
  companyId,
  onCreated,
}: {
  isOpen: boolean
  close: () => void
  companyId: string
  onCreated?: (driverId: number) => void
}) => {
  const { t } = useLocalize()
  const { addDriver, activateDriver } = useDriverService()
  const loading = useLoadingStore(selectLoading)
  const { triggerRefresh, refresh } = useDrivers()
  const {
    formType,
    isFullForm,
    data,
    isActive,
    setIsReadyToSubmit,
    isOpenGroupsModal,
    setIsOpenAddDriverModal,
    setData,
    isEditState,
    setCompanyIdState,
  } = useDriverForm()

  const modalRef: any = React.useRef(null)

  const [submitted, setSubmitted] = React.useState(false)
  const addDriverSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setSubmitted(true)

    if (!modalRef.current) return
    const form = modalRef.current as HTMLFormElement

    const allValid = isEditState || isDriverFormValid(data, formType)
    setIsReadyToSubmit(allValid)

    if (!allValid) {
      // paint red borders on the empty required fields as a visual hint
      validateRequiredFields(form, true)
      toast.error({ text: t('driverModule.missing-required-fields') })
      return
    }

    const formattedData = {
      ...data,
      isActive,
    }

    try {
      if (isEditState) {
        await activateDriver({
          driversIds: [data?.driverId?.value as any],
          companyId,
          setActive: isActive,
        })
        await addDriver({ companyId, driver: formattedData })
        toast.success({ text: t('common.saved-successfully') })
        const editedId = Number(data?.driverId?.value)
        if (Number.isFinite(editedId)) onCreated?.(editedId)
        close()
        triggerRefresh()
        refresh()
        return
      }

      const res = await addDriver({
        companyId,
        driver: {
          driverId: {
            value: '0',
          },
          driverCard: {
            cardNumber: { value: data?.cardNumber },
            cardValidTo: data?.cardValidTo,
          },
          ...formattedData,
        },
      })

      toast.success({ text: t('common.saved-successfully') })
      const newId = extractCreatedDriverId(res)
      if (newId != null) onCreated?.(newId)
      close()
      triggerRefresh()
      refresh()
    } catch (err) {
      // Osoba z dostępem tylko do wybranych grup musi przypisać kierowcę do jednej z nich.
      const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code
      toast.error({
        text:
          code === 'DRIVER_GROUP_REQUIRED'
            ? t('driverModule.group-required')
            : isEditState
              ? t('driverModule.save-changes-failed')
              : t('driverModule.add-driver-failed'),
      })
    }
  }

  const validateRequiredFields = (
    form: HTMLFormElement | null,
    showErrors = false,
  ): boolean => {
    if (!form) return false

    const requiredFields = form.querySelectorAll('[required]')
    let allValid = true

    requiredFields.forEach((field: any) => {
      let isEmpty = false

      if (field.type === 'radio' || field.type === 'checkbox') {
        const groupName = field.name
        const groupChecked = form.querySelector(
          `input[name="${groupName}"]:checked`,
        )
        isEmpty = !groupChecked
      } else {
        const value = field.value?.trim() || ''
        isEmpty = !value
      }

      if (isEmpty) {
        allValid = false
        if (showErrors) {
          field.classList.add('tw-border-red-500')
        }
      } else {
        field.classList.remove('tw-border-red-500')
      }
    })

    return allValid
  }

  React.useEffect(() => {
    if (!isOpen) return
    const allValid = isEditState || isDriverFormValid(data, formType)
    setIsReadyToSubmit(allValid)
    if (submitted && modalRef.current) {
      validateRequiredFields(modalRef.current, true)
    }
  }, [isOpen, data, formType, isEditState, submitted])

  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }

    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  React.useEffect(() => {
    if (!isOpen) return
    setCompanyIdState(companyId)
  }, [isOpen, companyId])

  // Wcześniej ten `return` stał nad hookami - po zmianie `isOpen` React dostawał
  // inną liczbę hooków niż w poprzednim renderze. Hooki lecą teraz zawsze, a to,
  // co ma się dziać wyłącznie przy otwartym modalu, pilnują strażniki w efektach.
  if (!isOpen) return null

  return (
    <ModalLayout>
      <form
        onSubmit={(e) => addDriverSubmit(e)}
        noValidate
        ref={modalRef}
        className="tw-relative tw-flex tw-max-h-[90vh] tw-w-full tw-max-w-[720px] tw-flex-col tw-rounded-lg tw-bg-white"
      >
        {loading && (
          <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-z-[200] tw-grid tw-select-none tw-place-items-center tw-bg-black/40">
            <LoaderIcon className="tw-animate-spin" />
          </div>
        )}

        <AddDriverHeader close={close} />
        <div className="tw-flex-1 tw-overflow-y-auto">
          {isFullForm ? (
            formType === 'full-additional' ? (
              <FullFormAdditional />
            ) : formType === 'full-basic' ? (
              <FullFormBasic />
            ) : null
          ) : (
            <BasicForm />
          )}
        </div>
        <AddDriverFooter />
      </form>
      {isOpenGroupsModal && (
        <GroupsFormProvider initialSelectedIds={data?.groups || []}>
          <GroupsModal
            companyId={companyId}
            onChange={(e) => setData({ ...data, groups: e })}
            driverName={(() => {
              const name = data?.name?.trim()
              if (!name) return null
              const surname = data?.surname?.trim()
              return formatDriverName({ name, surname })
            })()}
            isOpen={isOpenGroupsModal}
            close={() => setIsOpenAddDriverModal(false)}
          />
        </GroupsFormProvider>
      )}
    </ModalLayout>
  )
}

export default AddDriverModal
